import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import {
  computeNatal,
  getGifts,
  requestNatalPdf,
  type NatalPlanetPlacement,
  type NatalReading,
} from '@/api/client';
import NatalWheel from '@/components/NatalWheel';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';
import { useUserStore } from '@/store/userStore';

/**
 * Premium natal-chart screen (250⭐, free for Priestess).
 *
 * Flow:
 *   1. If the user is missing birth_time / birth_place / birth_lat /
 *      birth_lon — show a soft nudge to fill them in Profile, with
 *      a single button that takes them there.
 *   2. Otherwise show a teaser (hero image + "what's inside" list +
 *      "Open for 250⭐" / "Included in Priestess" CTA).
 *   3. On submit — pay (if needed) → compute → render wheel + text.
 */

const SIGN_LABELS_RU: Record<NatalPlanetPlacement['sign'], string> = {
  aries: 'Овен', taurus: 'Телец', gemini: 'Близнецы', cancer: 'Рак',
  leo: 'Лев', virgo: 'Дева', libra: 'Весы', scorpio: 'Скорпион',
  sagittarius: 'Стрелец', capricorn: 'Козерог', aquarius: 'Водолей',
  pisces: 'Рыбы',
};
const SIGN_LABELS_EN: Record<NatalPlanetPlacement['sign'], string> = {
  aries: 'Aries', taurus: 'Taurus', gemini: 'Gemini', cancer: 'Cancer',
  leo: 'Leo', virgo: 'Virgo', libra: 'Libra', scorpio: 'Scorpio',
  sagittarius: 'Sagittarius', capricorn: 'Capricorn', aquarius: 'Aquarius',
  pisces: 'Pisces',
};
const PLANET_LABELS_RU: Record<NatalPlanetPlacement['planet'], string> = {
  sun: 'Солнце', moon: 'Луна', mercury: 'Меркурий', venus: 'Венера',
  mars: 'Марс', jupiter: 'Юпитер', saturn: 'Сатурн', uranus: 'Уран',
  neptune: 'Нептун', pluto: 'Плутон',
};
const PLANET_LABELS_EN: Record<NatalPlanetPlacement['planet'], string> = {
  sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus',
  mars: 'Mars', jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus',
  neptune: 'Neptune', pluto: 'Pluto',
};

const PLANET_GLYPHS: Record<NatalPlanetPlacement['planet'], string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
};

export default function NatalChart() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const navigate = useNavigate();

  const user = useUserStore((s) => s.user);
  const [reading, setReading] = useState<NatalReading | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGift, setHasGift] = useState(false);

  const { pay, paying, payError } = usePayment('natal_chart');
  const { isPriestess } = useSubscription();

  // PDF delivery — independent product, "lifetime unlock". Once paid,
  // user can re-deliver forever. The PDF lands in the user's chat with
  // the bot (not as a browser download) because Telegram WebView on
  // iOS can't reliably trigger file saves.
  const pdfPayment = usePayment('natal_chart_pdf');
  const [hasPdfUnlock, setHasPdfUnlock] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfSent, setPdfSent] = useState(false);

  useEffect(() => {
    getGifts()
      .then((g) => {
        setHasGift(g.products.includes('natal_chart'));
        setHasPdfUnlock(g.products.includes('natal_chart_pdf'));
      })
      .catch(() => {});
  }, []);

  // Gate: do we have everything we need to compute the chart?
  const missingBirthData =
    !user?.birth_time ||
    !user?.birth_place ||
    user?.birth_lat == null ||
    user?.birth_lon == null;

  const signLabel = (sign: NatalPlanetPlacement['sign']) =>
    lang === 'ru' ? SIGN_LABELS_RU[sign] : SIGN_LABELS_EN[sign];
  const planetLabel = (p: NatalPlanetPlacement['planet']) =>
    lang === 'ru' ? PLANET_LABELS_RU[p] : PLANET_LABELS_EN[p];

  const run = async () => {
    setError(null);
    try {
      // The endpoint reads profile values by default — no payload needed
      // when the user already filled them in. We send nothing so the
      // server uses what's stored.
      const r = await computeNatal({});
      setReading(r);
      // Scroll to the top of the result so the wheel is the first thing
      // the user sees, not the spinner area.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onSubmit = () => {
    if (busy || missingBirthData) return;
    if (isPriestess || hasGift) {
      setBusy(true);
      run().finally(() => setBusy(false));
      return;
    }
    pay(run);
  };

  const reset = () => {
    setReading(null);
    setError(null);
  };

  /** Trigger PDF generation. The backend renders the PDF and sends it
   *  as a document to the user's chat with the bot — there is no
   *  blob/download flow on this client. We just show a success state
   *  after the API acknowledges delivery. */
  const sendPdfToChat = async () => {
    setPdfError(null);
    setPdfBusy(true);
    try {
      await requestNatalPdf();
      setPdfSent(true);
      // Once delivered, future deliveries are free (lifetime unlock).
      setHasPdfUnlock(true);
      // Auto-hide the success banner after a while so the screen stays
      // tidy if they keep scrolling. The button itself stays.
      setTimeout(() => setPdfSent(false), 12000);
    } catch (e) {
      setPdfError((e as Error).message);
    } finally {
      setPdfBusy(false);
    }
  };

  const onPdfClick = () => {
    if (pdfBusy || pdfPayment.paying) return;
    if (isPriestess || hasPdfUnlock) {
      sendPdfToChat();
      return;
    }
    pdfPayment.pay(sendPdfToChat);
  };

  /** Close the WebApp and return the user to their chat with the bot —
   *  natural CTA after the PDF lands there. Falls back to noop outside
   *  Telegram (dev / browser previews). */
  const openBotChat = () => {
    const tg = window.Telegram?.WebApp;
    if (tg?.close) tg.close();
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        ✦ {t('natal.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('natal.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!reading ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {/* Hero banner — atmospheric only, no text overlay. */}
            <div className="mb-4 overflow-hidden rounded-2xl border border-gold-500/30">
              <img
                src="/natal/hero.jpeg"
                alt=""
                className="block h-44 w-full object-cover"
                loading="lazy"
              />
            </div>

            {missingBirthData ? (
              // Gentle nudge to fill the natal inputs first.
              <OrnateFrame className="card-glow mb-4">
                <p className="mb-3 font-serif italic leading-relaxed text-gold-200">
                  {t('natal.need_birth_data')}
                </p>
                <ul className="mb-4 space-y-1 text-xs text-gold-300/70">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-500/80">•</span>
                    <span>{t('natal.need_time')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-500/80">•</span>
                    <span>{t('natal.need_place')}</span>
                  </li>
                </ul>
                <button
                  type="button"
                  // `?edit=natal` tells Profile to open the edit form
                  // automatically — saves the user one click and one
                  // "where do I edit this?" moment of confusion.
                  onClick={() => navigate('/profile?edit=natal')}
                  className="btn-primary w-full"
                >
                  {t('natal.go_to_profile')}
                </button>
              </OrnateFrame>
            ) : (
              <>
                <OrnateFrame className="card-glow mb-4">
                  <p className="text-center font-serif italic leading-relaxed text-gold-200">
                    {t('natal.description')}
                  </p>
                </OrnateFrame>

                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={busy || paying}
                  className="btn-primary w-full disabled:opacity-40"
                >
                  {paying || busy
                    ? t('payment.opening')
                    : isPriestess
                      ? t('natal.open_priestess')
                      : hasGift
                        ? t('natal.open_gift')
                        : t('natal.open_for_stars', { stars: 250 })}
                </button>
                {isPriestess && (
                  <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                    ✦ {t('spread.included_priestess')}
                  </p>
                )}
                {!isPriestess && hasGift && (
                  <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-emerald-300/80">
                    🎁 {t('spread.gift_explain')}
                  </p>
                )}
                {(payError || error) && (
                  <p className="mt-3 text-center text-xs text-rose-400">
                    {payError ?? error}
                  </p>
                )}

                {/* What's inside teaser */}
                <ul className="mt-5 space-y-1.5 text-xs text-gold-300/70">
                  {[
                    'inside_asc',
                    'inside_big_three',
                    'inside_personal',
                    'inside_social',
                    'inside_aspects',
                    'inside_focus',
                  ].map((k) => (
                    <li key={k} className="flex items-start gap-2">
                      <span className="text-gold-500/80">✦</span>
                      <span>{t(`natal.${k}`)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {reading.from_cache && (
              <div className="mb-3 rounded-lg border border-gold-500/30
                              bg-gold-500/10 px-3 py-2 text-[12px] leading-snug
                              text-gold-200">
                ✦ {t('natal.from_cache')}
              </div>
            )}
            {reading.truncated && (
              <div className="mb-3 rounded-lg border border-rose-400/40
                              bg-rose-500/10 px-3 py-2.5 text-[12px] leading-snug
                              text-rose-200">
                <p className="mb-2">⚠ {t('natal.truncated')}</p>
                <button
                  type="button"
                  onClick={() => { reset(); onSubmit(); }}
                  className="rounded-full border border-gold-500/40 bg-night-900/50
                             px-3 py-1 text-[11px] uppercase tracking-widest
                             text-gold-200 transition hover:border-gold-400"
                >
                  ↻ {t('natal.retry')}
                </button>
              </div>
            )}

            {/* The wheel — scales up from 0.9 with a 1.2s ease so the
                user gets a "the heavens are revealing themselves" beat,
                not just a static screenshot. Matches the demo intro. */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="mb-4 flex justify-center"
            >
              <NatalWheel chart={reading.metadata} size={340} />
            </motion.div>

            {/* Compact planet placements list — cascades in row by row
                AFTER the wheel finishes scaling. 1.2s delay matches the
                wheel duration; 0.08s per planet gives a brisk-but-still-
                perceptible ripple through all ten. */}
            <OrnateFrame className="mb-4">
              <ul className="space-y-1.5 text-sm text-gold-200">
                {reading.metadata.planets.map((p, i) => (
                  <motion.li
                    key={p.planet}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.2 + i * 0.08, duration: 0.35 }}
                    className="flex items-center gap-3"
                  >
                    <span className="w-6 font-serif text-base text-gold-300">
                      {PLANET_GLYPHS[p.planet]}
                    </span>
                    <span className="flex-1 font-serif">
                      <span className="text-gold-300">{planetLabel(p.planet)}</span>
                      <span className="text-gold-400/70"> · {signLabel(p.sign)}</span>
                      <span className="text-gold-400/60"> · {p.house}{t('natal.house_suffix')}</span>
                    </span>
                    {p.retrograde && (
                      <span className="text-[10px] uppercase tracking-widest text-rose-300/80">
                        R
                      </span>
                    )}
                  </motion.li>
                ))}
              </ul>
            </OrnateFrame>

            {/* AI interpretation — 10 sections of bold-headed text */}
            <OrnateFrame className="card-glow mb-4">
              <RichText
                text={reading.interpretation}
                className="font-serif italic leading-relaxed text-gold-200"
              />
            </OrnateFrame>

            {/* PDF delivery — separate paid product, lifetime unlock.
                File arrives as a Telegram document in the user's chat
                with the bot, not as a browser download. */}
            <OrnateFrame className="mb-4">
              <p className="mb-3 text-center font-serif italic text-gold-300/90">
                {t('natal.pdf_description')}
              </p>

              {/* Success banner — shows for ~12s after PDF is delivered */}
              {pdfSent && (
                <div className="mb-3 rounded-lg border border-emerald-400/40
                                bg-emerald-500/10 px-3 py-3 text-center">
                  <p className="mb-2 text-sm text-emerald-200">
                    ✓ {t('natal.pdf_sent_to_chat')}
                  </p>
                  <button
                    type="button"
                    onClick={openBotChat}
                    className="rounded-full border border-emerald-400/50
                               bg-night-900/40 px-4 py-1.5 text-[11px]
                               uppercase tracking-widest text-emerald-200
                               transition hover:border-emerald-300"
                  >
                    {t('natal.pdf_open_chat')}
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={onPdfClick}
                disabled={pdfBusy || pdfPayment.paying}
                className="btn-primary w-full disabled:opacity-40"
              >
                {pdfBusy
                  ? t('natal.pdf_preparing')
                  : pdfPayment.paying
                    ? t('payment.opening')
                    : isPriestess
                      ? t('natal.pdf_send_priestess')
                      : hasPdfUnlock
                        ? t('natal.pdf_send_again')
                        : t('natal.pdf_buy', { stars: 150 })}
              </button>
              {isPriestess && (
                <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                  ✦ {t('spread.included_priestess')}
                </p>
              )}
              {!isPriestess && hasPdfUnlock && (
                <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-emerald-300/80">
                  ✓ {t('natal.pdf_already_owned')}
                </p>
              )}
              {(pdfPayment.payError || pdfError) && (
                <p className="mt-3 text-center text-xs text-rose-400">
                  {pdfPayment.payError ?? pdfError}
                </p>
              )}
            </OrnateFrame>

            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" onClick={reset} className="nav-back-pill">
                <span>←</span>
                <span>{t('natal.back')}</span>
              </button>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('reading.scroll_to_top')}
                className="nav-scroll-top"
              >
                ↑
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
